import { normalizeAttackIntervalMs } from "./attack-interval";
import { normalizeModuleSkillRanges, normalizeSkillRange } from "./skill-range";
import type {
  AbilityCategory,
  CombatDefinition,
  CompanionDefinition,
  EconomyDefinition,
  GrowthDefinition,
  HealingDefinition,
  ModuleDefinition,
  OneShotDefinition,
  SynergyDefinition,
  SynergyRequirement,
} from "./phase-three-types";

export const SKILL_POOL_EXPANSION_ID_PREFIX = "branch_";

export const SKILL_POOL_EXPANSION_EXCLUDED_CATEGORIES = [
  "archaeology",
  "finance",
  "pasture",
  "mining",
] as const satisfies readonly AbilityCategory[];

const EXCLUDED_CATEGORY_SET = new Set<AbilityCategory>(
  SKILL_POOL_EXPANSION_EXCLUDED_CATEGORIES,
);

const NAME_PREFIXES: Readonly<
  Record<
    Exclude<
      AbilityCategory,
      "archaeology" | "finance" | "pasture" | "mining"
    >,
    readonly string[]
  >
> = {
  attack: ["追猎", "破阵", "裂风", "贯星"],
  area: ["涡流", "天幕", "回响", "潮界"],
  elite_attack: ["裁决", "断冠", "猎王", "肃清"],
  labor: ["巧作", "奋进", "百工", "奔业"],
  enhancement: ["淬炼", "铸魂", "铭刻", "锻体"],
  follower: ["同契", "伴星", "影侍", "灵盟"],
  summon: ["门庭", "唤潮", "军势", "降临"],
  healing: ["复苏", "回春", "护命", "生息"],
  growth: ["蜕变", "演化", "积势", "萌发"],
  one_shot: ["赐福", "秘仪", "启示", "馈赠"],
  guard: ["壁垒", "镇守", "铁卫", "不动"],
  special: ["异兆", "奇仪", "秘律", "逆理"],
};

const LABOR_TRIGGERS = [
  "move_distance",
  "attack",
  "repair",
  "excavate",
] as const;

const GROWTH_EVENTS = [
  "second",
  "move_distance",
  "attack",
  "kill",
  "repair",
  "excavate",
  "companion_income",
] as const;

const GROWTH_STATS = [
  "damage",
  "maxHealth",
  "armor",
  "moveSpeed",
  "manaMax",
] as const;

export function isSkillPoolExpansionCategory(
  category: AbilityCategory,
): boolean {
  return !EXCLUDED_CATEGORY_SET.has(category);
}

export function getSkillPoolExpansionId(sourceId: string): string {
  return `${SKILL_POOL_EXPANSION_ID_PREFIX}${sourceId}`;
}

export function getSkillPoolExpansionSourceId(
  moduleId: string,
): string | undefined {
  return moduleId.startsWith(SKILL_POOL_EXPANSION_ID_PREFIX)
    ? moduleId.slice(SKILL_POOL_EXPANSION_ID_PREFIX.length)
    : undefined;
}

export function createSkillPoolExpansion(
  sourceModules: readonly ModuleDefinition[],
): ModuleDefinition[] {
  const eligibleModules = sourceModules.filter((module) =>
    isSkillPoolExpansionCategory(module.category),
  );
  const sourceIds = new Set(sourceModules.map((module) => module.id));
  const variantIds = new Set(
    eligibleModules.map((module) => getSkillPoolExpansionId(module.id)),
  );

  for (const module of eligibleModules) {
    if (sourceIds.has(getSkillPoolExpansionId(module.id))) {
      throw new Error(`技能扩充ID与已有技能冲突: ${module.id}`);
    }
  }
  if (variantIds.size !== eligibleModules.length) {
    throw new Error("技能扩充ID不唯一。");
  }

  return eligibleModules.map((source) => createBranchModule(source));
}

export function expandSynergiesForSkillPool(
  synergies: readonly SynergyDefinition[],
  modules: readonly ModuleDefinition[],
): SynergyDefinition[] {
  const moduleIds = new Set(modules.map((module) => module.id));
  const withBranchAlternative = (moduleId: string): string[] => {
    const branchId = getSkillPoolExpansionId(moduleId);
    return moduleIds.has(branchId) ? [moduleId, branchId] : [moduleId];
  };

  return synergies.map((synergy) => ({
    ...synergy,
    requirements: synergy.requirements.map(
      (requirement): SynergyRequirement => {
        if (requirement.kind === "module") {
          const alternatives = withBranchAlternative(requirement.moduleId);
          return alternatives.length === 1
            ? requirement
            : { kind: "one_of", moduleIds: alternatives };
        }
        if (requirement.kind === "one_of") {
          return {
            ...requirement,
            moduleIds: [
              ...new Set(
                requirement.moduleIds.flatMap(withBranchAlternative),
              ),
            ],
          };
        }
        return requirement;
      },
    ),
    abilityModifiers: synergy.abilityModifiers?.map((modifier) => ({
      ...modifier,
      moduleIds: [
        ...new Set(modifier.moduleIds.flatMap(withBranchAlternative)),
      ],
    })),
  }));
}

function createBranchModule(source: ModuleDefinition): ModuleDefinition {
  const seedSource =
    source.tier === 6 && source.evolutionFrom
      ? source.evolutionFrom
      : source.id;
  const seed = stableHash(seedSource);
  const prefixes =
    NAME_PREFIXES[
      source.category as keyof typeof NAME_PREFIXES
    ];
  if (!prefixes) {
    throw new Error(`不应扩充的技能类型: ${source.category}`);
  }

  let branch = cloneModule(source);
  branch.id = getSkillPoolExpansionId(source.id);
  branch.name = `${prefixes[seed % prefixes.length]}·${source.name}`;
  branch.description = "";
  branch.tags = [
    ...new Set([
      ...source.tags,
      "expanded_pool",
      "branch_variant",
      `branch_${source.category}`,
    ]),
  ];
  branch.weight = source.tier === 6 ? 0 : source.weight;
  branch.evolutionTo = source.evolutionTo
    ? getSkillPoolExpansionId(source.evolutionTo)
    : undefined;
  branch.evolutionFrom = source.evolutionFrom
    ? getSkillPoolExpansionId(source.evolutionFrom)
    : undefined;

  switch (source.category) {
    case "attack":
    case "area":
    case "elite_attack":
      branch.combat = tuneCombat(source.combat!, source.category, source.tier, seed);
      break;
    case "labor":
      branch.economy = tuneLabor(source.economy!, source.tier, seed);
      break;
    case "enhancement":
      branch.bonuses = tuneEnhancement(branch.bonuses, source.tier, seed);
      break;
    case "follower":
    case "summon":
    case "guard":
      branch.companion = tuneCompanion(
        source.companion!,
        source.tier,
        seed,
      );
      branch.bonuses =
        source.category === "summon"
          ? {
              ...branch.bonuses,
              manaMaxAdd: (branch.bonuses.manaMaxAdd ?? 0) + source.tier * 3,
              summonCapAdd: (branch.bonuses.summonCapAdd ?? 0) + 1,
            }
          : branch.bonuses;
      break;
    case "healing":
      branch.healing = tuneHealing(source.healing!, source.tier, seed);
      break;
    case "growth":
      branch.growth = tuneGrowth(source.growth!, source.tier, seed);
      break;
    case "one_shot":
      branch.onAcquire = tuneOneShot(source.onAcquire!, source.tier, seed);
      break;
    case "special":
      branch.bonuses = tuneSpecial(branch.bonuses, source.tier, seed);
      break;
    case "archaeology":
    case "finance":
    case "pasture":
      throw new Error(`不应扩充的技能类型: ${source.category}`);
  }

  branch = normalizeModuleSkillRanges(branch);
  return branch;
}

function tuneCombat(
  source: CombatDefinition,
  category: "attack" | "area" | "elite_attack",
  tier: number,
  seed: number,
): CombatDefinition {
  const mode = seed % 4;
  const direction = mode % 2 === 0 ? 1 : -1;
  const intervalFactors = [0.8, 1.2, 0.75, 1.35] as const;
  const projectiles = source.projectiles ?? 1;
  const nextProjectiles =
    mode === 0
      ? projectiles + 1
      : mode === 2 && projectiles > 1
        ? projectiles - 1
        : projectiles;
  const rangeDelta = direction * (30 + tier * 10);
  const radiusDelta = -direction * (15 + tier * 5);

  return {
    ...source,
    intervalMs: normalizeAttackIntervalMs(
      source.intervalMs * intervalFactors[mode],
    ),
    range: normalizeSkillRange(Math.max(5, source.range + rangeDelta)),
    target:
      category === "attack"
        ? source.target === "closest"
          ? "random"
          : "closest"
        : category === "area"
          ? "random_area"
          : mode === 2
            ? "boss_only"
            : "elite_only",
    radius:
      source.radius === undefined
        ? category === "area"
          ? normalizeSkillRange(55 + tier * 20)
          : undefined
        : normalizeSkillRange(Math.max(5, source.radius + radiusDelta)),
    projectiles: nextProjectiles,
    chainTargets:
      category === "attack" && mode === 1
        ? (source.chainTargets ?? 0) + 1
        : source.chainTargets,
    pierceTargets:
      category === "elite_attack"
        ? (source.pierceTargets ?? 0) + 1
        : source.pierceTargets,
    slowRatio:
      source.slowRatio === undefined
        ? category === "area" && mode === 3
          ? 0.12 + tier * 0.01
          : undefined
        : clamp(source.slowRatio + direction * 0.03, 0.05, 0.65),
    slowDurationMs:
      source.slowRatio === undefined && category === "area" && mode === 3
        ? 1_200 + tier * 100
        : source.slowDurationMs,
    manaCost:
      source.manaCost === undefined
        ? undefined
        : Math.max(1, Math.round(source.manaCost * (direction > 0 ? 1.15 : 0.9))),
  };
}

function tuneCompanion(
  source: CompanionDefinition,
  tier: number,
  seed: number,
): CompanionDefinition {
  const mode = seed % 4;
  const direction = mode % 2 === 0 ? 1 : -1;
  const intervalFactors = [0.8, 1.2, 0.75, 1.35] as const;
  const hasAttack =
    source.damage !== undefined && source.intervalMs !== undefined;

  return {
    ...source,
    count: source.count + (mode === 0 || mode === 3 ? 1 : 0),
    orbitRadius: normalizeSkillRange(
      Math.max(5, source.orbitRadius + direction * (15 + tier * 5)),
    ),
    moveSpeed: Math.max(
      40,
      Math.round(source.moveSpeed * (direction > 0 ? 1.1 : 0.92)),
    ),
    intervalMs: hasAttack
      ? normalizeAttackIntervalMs(
          source.intervalMs! * intervalFactors[mode],
        )
      : source.intervalMs,
    range:
      source.range === undefined
        ? undefined
        : normalizeSkillRange(
            Math.max(5, source.range - direction * (30 + tier * 10)),
          ),
    health:
      source.health === undefined
        ? undefined
        : Math.max(
            1,
            Math.round(source.health * (direction > 0 ? 0.9 : 1.18)),
          ),
    armor:
      source.armor === undefined
        ? undefined
        : Math.max(0, source.armor + (direction > 0 ? -tier : tier + 1)),
    manaCost:
      source.manaCost === undefined
        ? undefined
        : Math.max(
            1,
            Math.round(source.manaCost * (direction > 0 ? 1.12 : 0.9)),
          ),
    spawnIntervalMs:
      source.spawnIntervalMs === undefined
        ? undefined
        : Math.max(
            1_000,
            source.spawnIntervalMs + (direction > 0 ? 1_000 : -1_000),
          ),
    respawnMs:
      source.respawnMs === undefined
        ? undefined
        : Math.max(
            1_000,
            source.respawnMs + (direction > 0 ? -1_000 : 1_000),
          ),
  };
}

function tuneLabor(
  source: EconomyDefinition,
  tier: number,
  seed: number,
): EconomyDefinition {
  const currentIndex = LABOR_TRIGGERS.indexOf(
    source.trigger as (typeof LABOR_TRIGGERS)[number],
  );
  const trigger =
    LABOR_TRIGGERS[
      (Math.max(0, currentIndex) + 1 + (seed % 3)) % LABOR_TRIGGERS.length
    ];
  const goldFocused = seed % 2 === 0;
  return {
    ...source,
    trigger,
    intervalMs: undefined,
    distance:
      trigger === "move_distance"
        ? Math.max(60, 190 - tier * 15)
        : undefined,
    gold: Math.max(
      1,
      Math.round((source.gold ?? tier + 1) * (goldFocused ? 1.35 : 0.85)),
    ),
    experience: Math.max(
      1,
      Math.round((source.experience ?? tier) * (goldFocused ? 0.8 : 1.4)),
    ),
    crystalChance:
      tier >= 3
        ? Math.max(0.005, (source.crystalChance ?? 0) + 0.005 * tier)
        : source.crystalChance,
  };
}

function tuneEnhancement(
  source: ModuleDefinition["bonuses"],
  tier: number,
  seed: number,
): ModuleDefinition["bonuses"] {
  const mode = seed % 4;
  const tuned = { ...source };
  if (mode === 0) {
    tuned.damageAdd = (tuned.damageAdd ?? 0) + tier * 4;
    tuned.pierceTargets = (tuned.pierceTargets ?? 0) + (tier >= 3 ? 1 : 0);
  } else if (mode === 1) {
    tuned.maxHealthAdd = (tuned.maxHealthAdd ?? 0) + tier * 18;
    tuned.healthRegenAdd = (tuned.healthRegenAdd ?? 0) + tier * 0.15;
  } else if (mode === 2) {
    tuned.armorAdd = (tuned.armorAdd ?? 0) + tier * 2;
    tuned.dodgeAdd = (tuned.dodgeAdd ?? 0) + tier * 0.006;
  } else {
    tuned.moveSpeedAdd = (tuned.moveSpeedAdd ?? 0) + tier * 5;
    tuned.fireIntervalMultiplier = Math.min(
      0.98,
      (tuned.fireIntervalMultiplier ?? 1) - tier * 0.012,
    );
  }
  return tuned;
}

function tuneHealing(
  source: HealingDefinition,
  tier: number,
  seed: number,
): HealingDefinition {
  const mode = seed % 4;
  const faster = mode % 2 === 0;
  const target =
    mode === 0 && tier >= 2
      ? "team"
      : mode === 2 && tier >= 4
        ? "core"
        : source.target;
  return {
    ...source,
    target,
    amount: Math.max(
      1,
      Math.round(source.amount * (faster ? 0.85 : 1.3)),
    ),
    intervalMs: Math.max(
      500,
      Math.round(
        (source.intervalMs * (faster ? 0.75 : 1.25)) / 100,
      ) * 100,
    ),
    range:
      source.range === undefined
        ? target === "self"
          ? undefined
          : normalizeSkillRange(300 + tier * 50)
        : normalizeSkillRange(
            Math.max(5, source.range + (faster ? -30 : 50)),
          ),
  };
}

function tuneGrowth(
  source: GrowthDefinition,
  tier: number,
  seed: number,
): GrowthDefinition {
  const eventIndex = GROWTH_EVENTS.indexOf(
    source.event as (typeof GROWTH_EVENTS)[number],
  );
  const faster = seed % 2 === 0;
  if (source.bonuses) {
    return {
      ...source,
      event:
        GROWTH_EVENTS[(eventIndex + 1 + (seed % 3)) % GROWTH_EVENTS.length],
      every: Math.max(1, Math.round(source.every * (faster ? 0.75 : 1.25))),
      bonuses: Object.fromEntries(
        Object.entries(source.bonuses).map(([stat, value]) => [
          stat,
          Math.max(1, Math.round(value * (faster ? 0.8 : 1.35))),
        ]),
      ),
      cap:
        source.cap === undefined
          ? tier * 20
          : Math.max(1, Math.round(source.cap * (faster ? 0.9 : 1.2))),
    };
  }
  const statIndex = GROWTH_STATS.indexOf(source.stat);
  return {
    ...source,
    event:
      GROWTH_EVENTS[(eventIndex + 1 + (seed % 3)) % GROWTH_EVENTS.length],
    stat: GROWTH_STATS[(statIndex + 1 + (seed % 2)) % GROWTH_STATS.length],
    every: Math.max(1, Math.round(source.every * (faster ? 0.75 : 1.25))),
    amount: Math.max(
      1,
      Math.round(source.amount * (faster ? 0.8 : 1.35)),
    ),
    cap:
      source.cap === undefined
        ? tier * 20
        : Math.max(1, Math.round(source.cap * (faster ? 0.9 : 1.2))),
  };
}

function tuneOneShot(
  source: OneShotDefinition,
  tier: number,
  seed: number,
): OneShotDefinition {
  const mode = seed % 4;
  if (mode === 0) {
    return {
      ...source,
      gold: Math.max(1, Math.round((source.gold ?? tier * 15) * 0.7)),
      experience: Math.max(
        1,
        Math.round((source.experience ?? tier * 24) * 1.45),
      ),
      talentPoints: (source.talentPoints ?? 0) + (tier >= 4 ? 1 : 0),
    };
  }
  if (mode === 1) {
    return {
      ...source,
      gold: Math.max(1, Math.round((source.gold ?? tier * 12) * 0.55)),
      maxHealth: (source.maxHealth ?? tier * 12) + tier * 18,
      armor: (source.armor ?? 0) + tier * 3,
    };
  }
  if (mode === 2) {
    return {
      ...source,
      experience: Math.max(
        1,
        Math.round((source.experience ?? tier * 20) * 0.8),
      ),
      damage: (source.damage ?? 0) + tier * 4,
      crystals: (source.crystals ?? 0) + (tier >= 3 ? 1 : 0),
    };
  }
  return {
    ...source,
    gold: Math.max(1, Math.round((source.gold ?? tier * 16) * 1.15)),
    experience: Math.max(
      1,
      Math.round((source.experience ?? tier * 20) * 0.65),
    ),
    maxHealth: (source.maxHealth ?? 0) + tier * 10,
  };
}

function tuneSpecial(
  source: ModuleDefinition["bonuses"],
  tier: number,
  seed: number,
): ModuleDefinition["bonuses"] {
  const mode = seed % 4;
  if (mode === 0) {
    return {
      ...source,
      moveSpeedAdd: (source.moveSpeedAdd ?? 0) + tier * 7,
      scoutingRadiusAdd: (source.scoutingRadiusAdd ?? 0) + tier * 40,
    };
  }
  if (mode === 1) {
    return {
      ...source,
      dodgeAdd: (source.dodgeAdd ?? 0) + tier * 0.01,
      crystalDropChance: (source.crystalDropChance ?? 0) + tier * 0.002,
    };
  }
  if (mode === 2) {
    return {
      ...source,
      manaMaxAdd: (source.manaMaxAdd ?? 0) + tier * 12,
      manaRegenAdd: (source.manaRegenAdd ?? 0) + tier * 0.2,
    };
  }
  return {
    ...source,
    maxHealthAdd: (source.maxHealthAdd ?? 0) + tier * 20,
    armorAdd: (source.armorAdd ?? 0) + tier * 2,
  };
}

function cloneModule(source: ModuleDefinition): ModuleDefinition {
  return {
    ...source,
    tags: [...source.tags],
    bonuses: { ...source.bonuses },
    combat: source.combat ? { ...source.combat } : undefined,
    economy: source.economy ? { ...source.economy } : undefined,
    companion: source.companion ? { ...source.companion } : undefined,
    growth: source.growth ? { ...source.growth } : undefined,
    onAcquire: source.onAcquire ? { ...source.onAcquire } : undefined,
    healing: source.healing ? { ...source.healing } : undefined,
    archaeology: source.archaeology ? { ...source.archaeology } : undefined,
  };
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
